import React, { useState } from "react";
import { ActivityIndicator, Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "expo-router";
import ScButton from "@/src/components/ui-kit/button";
import {
  useAuthorFollow,
  useFollowingList,
  useFollowingScope,
  useFollowerPreferences,
  useFollowerInbox,
} from "./hooks";
import { readCounts, markFollowerRead, reader } from "./service";
export function FollowButton({ authorId }: { authorId: string }) {
  const f = useAuthorFollow(authorId);
  const { t } = useTranslation();
  if (!f.available || !authorId || authorId === f.viewer) return null;
  const unavailable = f.control.isError;
  return (
    <View className="gap-1">
      <ScButton
        style={{
          minHeight: 44,
        }}
        text={
          unavailable
            ? "FOLLOW_UNAVAILABLE"
            : f.state?.validationState === "rejected" &&
                f.control.data?.desiredFollowing
              ? "FOLLOW_UNFOLLOW"
              : f.following
                ? "FOLLOW_FOLLOWING"
                : "FOLLOW_ACTION"
        }
        mode={f.following ? "outlined" : "contained"}
        disabled={!f.ready}
        onPress={() =>
          f.mutation.mutate({
            desiredFollowing: !f.following,
          })
        }
      />
      {(f.mutation.isPending ||
        (f.following &&
          (!f.state || f.state.validationState === "pending"))) && (
        <Text accessibilityLiveRegion="polite" className="text-xs text-slate">
          {t("FOLLOW_PENDING")}
        </Text>
      )}
      {(f.mutation.isError ||
        unavailable ||
        f.state?.validationState === "rejected") && (
        <View>
          <Text
            accessibilityLiveRegion="polite"
            className="text-xs text-crimson"
          >
            {t(
              f.state?.validationState === "rejected"
                ? "FOLLOW_UNAVAILABLE"
                : "FOLLOW_RECONCILE",
            )}
          </Text>
          <ScButton
            text="BTN_RETRY"
            mode="text"
            onPress={() => {
              void f.control.refetch();
            }}
          />
        </View>
      )}
    </View>
  );
}
function ChannelSwitch({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityLabel={t(label)}
      accessibilityState={{
        checked: value,
        disabled,
      }}
      disabled={disabled}
      onPress={() => onChange(!value)}
      style={{
        minHeight: 44,
      }}
      className="flex-row items-center justify-between gap-4"
    >
      <Text className="text-sm text-onyx flex-1">{t(label)}</Text>
      <Text className={value ? "text-primary font-semibold" : "text-slate"}>
        {t(value ? "FOLLOW_ON" : "FOLLOW_OFF")}
      </Text>
    </Pressable>
  );
}
function AuthorChannels({ authorId }: { authorId: string }) {
  const f = useAuthorFollow(authorId);
  if (!f.control.data?.desiredFollowing) return null;
  return (
    <View>
      <ChannelSwitch
        label="FOLLOW_APP_ALERTS"
        value={f.control.data.appNotificationsEnabled}
        disabled={!f.ready}
        onChange={(v) =>
          f.mutation.mutate({
            appNotificationsEnabled: v,
          })
        }
      />
      <ChannelSwitch
        label="FOLLOW_DIGEST"
        value={f.control.data.digestEnabled}
        disabled={!f.ready}
        onChange={(v) =>
          f.mutation.mutate({
            digestEnabled: v,
          })
        }
      />
    </View>
  );
}
export function FollowingList() {
  const { t } = useTranslation();
  const q = useFollowingList(),
    s = useFollowingScope(),
    router = useRouter();
  if (!s.available) return null;
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <View className="gap-4 p-4">
      <Text className="text-lg font-semibold text-onyx">
        {t("FOLLOW_YOUR_LIST")}
      </Text>
      {q.isPending && <ActivityIndicator />}
      {q.isError && (
        <ScButton
          text="BTN_RETRY"
          onPress={() => {
            void q.refetch();
          }}
        />
      )}
      {!q.isPending && !q.isError && !rows.length && (
        <Text className="text-slate">{t("FOLLOW_NONE")}</Text>
      )}
      {rows.map((row) => (
        <View
          key={row.authorId}
          className="border border-ash-3 rounded-xl p-4 gap-2"
        >
          <Pressable
            style={{
              minHeight: 44,
            }}
            accessibilityRole="link"
            onPress={() =>
              router.push({
                pathname: "/profile/[id]",
                params: {
                  id: row.authorId,
                },
              })
            }
          >
            <Text className="font-semibold text-onyx">{row.displayName}</Text>
          </Pressable>
          <FollowButton authorId={row.authorId} />
          <AuthorChannels authorId={row.authorId} />
        </View>
      ))}
      {q.hasNextPage && (
        <ScButton
          text="BTN_LOAD_MORE"
          loading={q.isFetchingNextPage}
          onPress={() => {
            void q.fetchNextPage();
          }}
        />
      )}
    </View>
  );
}
export function FollowProfile({
  authorId,
  own,
}: {
  authorId: string;
  own: boolean;
}) {
  const s = useFollowingScope(),
    { t } = useTranslation(),
    [open, setOpen] = useState(false);
  const counts = useQuery({
    queryKey: [...s.key, "counts", authorId],
    queryFn: () => readCounts([authorId]),
    enabled: s.available && !!authorId,
    retry: false,
    staleTime: 60000,
  });
  if (!s.available) return null;
  const n = counts.data?.items[0];
  return (
    <View className="gap-3">
      <View className="flex-row flex-wrap gap-4">
        <Text className="text-sm text-slate">
          {t("FOLLOW_FOLLOWERS_COUNT", {
            count: n?.followerCount ?? "—",
          })}
        </Text>
        <Pressable
          disabled={!own}
          accessibilityRole={own ? "button" : "text"}
          style={{
            minHeight: 44,
          }}
          onPress={() => setOpen(!open)}
        >
          <Text className="text-sm text-slate">
            {t("FOLLOW_FOLLOWING_COUNT", {
              count: n?.followingCount ?? "—",
            })}
          </Text>
        </Pressable>
      </View>
      {!own && <FollowButton authorId={authorId} />}
      {own && open && <FollowingList />}
    </View>
  );
}
export function FollowingPreferences() {
  const f = useFollowerPreferences(),
    { t } = useTranslation();
  if (!f.available) return null;
  return (
    <View className="gap-2 mt-6">
      <Text className="font-semibold text-onyx">{t("FOLLOW_SETTINGS")}</Text>
      <Text className="text-sm text-slate">{t("FOLLOW_SETTINGS_HINT")}</Text>
      {f.query.isPending ? (
        <ActivityIndicator />
      ) : f.query.isError ? (
        <ScButton
          text="BTN_RETRY"
          onPress={() => {
            void f.query.refetch();
          }}
        />
      ) : (
        <>
          <ChannelSwitch
            label="FOLLOW_APP_ALERTS"
            value={
              f.mutation.isPending &&
              f.mutation.variables?.appNotificationsEnabled !== undefined
                ? f.mutation.variables.appNotificationsEnabled
                : f.query.data?.appNotificationsEnabled !== false
            }
            disabled={f.mutation.isPending}
            onChange={(v) =>
              f.mutation.mutate({
                appNotificationsEnabled: v,
              })
            }
          />
          <ChannelSwitch
            label="FOLLOW_DIGEST"
            value={
              f.mutation.isPending &&
              f.mutation.variables?.digestEnabled !== undefined
                ? f.mutation.variables.digestEnabled
                : f.query.data?.digestEnabled !== false
            }
            disabled={f.mutation.isPending}
            onChange={(v) =>
              f.mutation.mutate({
                digestEnabled: v,
              })
            }
          />
        </>
      )}
      {f.mutation.isError && (
        <Text accessibilityLiveRegion="polite" className="text-crimson">
          {t("FOLLOW_RECONCILE")}
        </Text>
      )}
    </View>
  );
}
export function FollowerInbox({
  unreadOnly = false,
}: {
  unreadOnly?: boolean;
}) {
  const { t } = useTranslation(),
    q = useFollowerInbox(unreadOnly),
    s = useFollowingScope(),
    router = useRouter(),
    client = useQueryClient();
  const [failed, setFailed] = useState(false);
  const mark = useMutation({
    mutationFn: (id: string) => markFollowerRead(s.viewer, s.org, id),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: s.key,
      }),
  });
  if (!s.available) return null;
  const items = q.isError ? [] : (q.data?.pages.flatMap((p) => p.items) ?? []);
  return (
    <View className="gap-2">
      {q.isError && (
        <ScButton
          text="FOLLOW_RETRY_ALERTS"
          mode="text"
          onPress={() => {
            void q.refetch();
          }}
        />
      )}
      {items.map((item) => (
        <View
          key={item.deliveryId}
          className="rounded-xl border border-ash-3 p-4"
        >
          <Pressable
            style={{
              minHeight: 44,
            }}
            accessibilityRole="link"
            onPress={async () => {
              try {
                setFailed(false);
                await reader({
                  action: "resolvePost",
                  postId: item.targetPostId,
                });
                if (!item.isRead) mark.mutate(item.deliveryId);
                router.push({
                  pathname: "/feeds",
                  params: {
                    post_id: item.targetPostId,
                    scope: "all",
                  },
                });
              } catch {
                setFailed(true);
                void q.refetch();
              }
            }}
          >
            <Text
              className={
                item.isRead ? "text-onyx" : "text-primary font-semibold"
              }
            >
              {t("FOLLOW_BATCH", {
                author: item.authorName,
                count: item.eligiblePostCount,
              })}
            </Text>
          </Pressable>
          {!item.isRead && (
            <ScButton
              text="BTN_MARK_AS_READ"
              mode="text"
              onPress={() => mark.mutate(item.deliveryId)}
            />
          )}
        </View>
      ))}
      {failed && (
        <Text accessibilityLiveRegion="polite" className="text-slate">
          {t("FOLLOW_UNAVAILABLE")}
        </Text>
      )}
      {q.hasNextPage && (
        <ScButton
          text="BTN_LOAD_MORE"
          loading={q.isFetchingNextPage}
          onPress={() => {
            void q.fetchNextPage();
          }}
        />
      )}
    </View>
  );
}
